/**
 * Open, print or save a ticket's PDF — ALWAYS the server's document,
 * `GET /tickets/:id/pdf`.
 *
 * ── WHY THIS IS ONE FUNCTION ────────────────────────────────────────────────
 *
 * The office editor's preview (`NewTicketScreen`) had its own "Print" and
 * "Save as PDF" buttons, and both were `window.print()` over the in-app HTML
 * preview. That preview is not the customer's document: it rendered a signed
 * approval stamp's signature as the WORD "Signed", and it carried none of what
 * the server PDF prints (api#995 added the recorded approval and "Signed copy
 * on file"). Smith FT-00062 "opened as a PDF" from the app therefore showed no
 * signature, while the same ticket's server PDF draws the ink.
 *
 * Every ticket document action in the app now goes through here, so there is
 * one place that decides which document a button produces. The ordering rules
 * — claim the tab inside the click, print through a tab on a phone — are
 * `lib/blob-tab.ts`'s, and are only called from here, never re-derived.
 */
import { api, getBlobApiErrorMessage } from '../api/client';
import { ticketPdfNeedsPreview } from './ticket-status';
import {
  claimBlobTab, showBlobInTab, saveBlobAs, POPUP_BLOCKED_MESSAGE,
  printsViaTab, printBlobInFrame, PRINT_IN_TAB_MESSAGE, PRINT_FAILED_MESSAGE,
} from './blob-tab';

export type TicketPdfAction = 'view' | 'print' | 'save';

export interface TicketPdfTarget {
  id: string;
  /** The status the screen is showing — decides real document vs watermarked preview. */
  status: string | null | undefined;
  ticketNumber: string | null | undefined;
}

export interface TicketPdfCallbacks {
  onError: (message: string) => void;
  /** A non-error note, e.g. "Opened — use your browser's print". */
  onNotice?: (message: string) => void;
}

/**
 * The server names a preview `ticket-FT-XXXXX-PREVIEW.pdf`. A saved file is
 * renamed here, so the suffix is carried across — otherwise a saved preview
 * lands in Downloads indistinguishable from the real document.
 */
export function ticketPdfFileName(ticketNumber: string | null | undefined, preview: boolean): string {
  return `${ticketNumber || 'ticket'}${preview ? '-PREVIEW' : ''}.pdf`;
}

/**
 * MUST be called synchronously from the click handler: the tab for View (and
 * for Print on a phone) is claimed before the first await.
 */
export async function runTicketPdfAction(
  action: TicketPdfAction,
  target: TicketPdfTarget,
  { onError, onNotice }: TicketPdfCallbacks,
): Promise<void> {
  const preview = ticketPdfNeedsPreview(target.status);
  const printTab = action === 'print' && printsViaTab();
  const needsTab = action === 'view' || printTab;

  // Claimed inside the gesture, or not at all.
  const win = needsTab ? claimBlobTab() : null;
  if (needsTab && !win) {
    onError(POPUP_BLOCKED_MESSAGE);
    return;
  }

  let blob: Blob;
  try {
    const res = await api.tickets.getPdf(target.id, { preview });
    blob = res.data;
  } catch (err) {
    // The tab was claimed before the request; never leave it on about:blank.
    win?.close();
    const message = await getBlobApiErrorMessage(err, 'Failed to generate PDF');
    // A 409 means the status moved under the page between load and click.
    onError((err as any)?.response?.status === 409 ? `${message} Refresh the page and try again.` : message);
    return;
  }

  if (win) {
    showBlobInTab(win, blob);
    if (printTab) onNotice?.(PRINT_IN_TAB_MESSAGE);
  } else if (action === 'print') {
    printBlobInFrame(blob).catch(() => onError(PRINT_FAILED_MESSAGE));
  } else {
    saveBlobAs(blob, ticketPdfFileName(target.ticketNumber, preview));
  }
}
